import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '../stylesheets/main.css';
import { getScrollbarWidth } from '../utils/Utils';
import AppContext from '../context/AppContext';
import Modal from '../components/organisms/modal/Modal';
import Notification from '../components/organisms/notification/Notification';
import ActivityList from '../pages/estimations/activityList/ActivityList';
import Login from '../pages/compte/login/Login';
import PolitiqueConfidentialite from '../pages/liensLegaux/politiqueConfidentialite/PolitiqueConfidentialite';
import MentionsLegales from '../pages/liensLegaux/mentionsLegales/MentionsLegales';
import Cgu from '../pages/liensLegaux/cgu/Cgu';
import Cgv from '../pages/liensLegaux/cgv/Cgv';
import Loader from '../components/organisms/loader/Loader';
import ErrorPage from '../pages/errors/errorPage/ErrorPage';
import { useWindowSize } from '@uidotdev/usehooks';
import DetailParcours from '../pages/estimations/parcours/parcours/Parcours';
import { fetchUserDataApi } from '../api/CompteApi';
import Overview from '../pages/estimations/parcours/overview/Overview';
import { CookiesProvider } from 'react-cookie';
import ParametrageCookies from '../pages/liensLegaux/parametrageCookie/ParametrageCookies';
import Pdf from '../pages/pdf/Pdf';
import Layout from '../components/organisms/layout/Layout';
import Access from '../pages/access/Access';
import RequireCode from '../pages/access/RequireCode';
import NotificationDownloadPdf from '../components/pdfComponents/notificationDownloadPdf/NotificationDownloadPdf';
import Account from '../pages/compte/account/Account';

function App() {
  const [modalVisible, setModalVisible] = useState(false);
  const [canHideOnClickOutside, setCanHideOnClickOutside] = useState(true);
  const [modalContent, setModalContent] = useState({ title: '', content: <></>, buttons: <></> });
  const [isPdfNotificationLoading, setIsPdfNotificationLoading] = useState(false); // chargement de la page
  const [appLoaderVisible, setAppLoaderVisible] = useState(false);
  const [mustAppLoaderShow, setMustAppLoaderShow] = useState(false);
  const createNotificationFunction = useRef(null);
  const { width } = useWindowSize();

  useEffect(() => {
    if (appLoaderVisible || (!appLoaderVisible && !mustAppLoaderShow)) togglePageScroll();
  }, [appLoaderVisible, mustAppLoaderShow]);

  useEffect(() => {
    document.body.style.setProperty('--scrollbar-width', getScrollbarWidth() + 'px');
  }, [width]);

  useEffect(() => {
    if (appLoaderVisible) {
      setMustAppLoaderShow(true);
      setTimeout(() => {
        setMustAppLoaderShow(false);
      }, 800);
    }
  }, [appLoaderVisible]);

  useEffect(() => {
    if (!modalVisible) {
      setCanHideOnClickOutside(true);
    }
  }, [modalVisible]);

  async function getUserData() {
    try {
      if (!getUserId()) return null;
      const res = (await fetchUserDataApi(getUserId())).data;

      delete res.uuid;
      return res;
    } catch (error) {
      createNotification(<>Erreur lors de la récupération des informations utilisateur</>, 'var(--red)', 'var(--grey)');
      return null;
    }
  }

  function setUserId(value) {
    if (value) {
      localStorage.setItem('uuid', value);
      getUserData();
    } else localStorage.removeItem('uuid');
  }

  function getUserId() {
    return localStorage.getItem('uuid');
  }

  function togglePageScroll() {
    if (appLoaderVisible) {
      document.body.classList.add('loading-page');
    } else {
      document.body.classList.remove('loading-page');
    }
  }

  function toggleBodyScroll(enable) {
    if (enable) document.body.classList.remove('non-scrollable-body');
    else document.body.classList.add('non-scrollable-body');
  }

  function createNotification(content, bgColor = 'var(--main-color)', color = 'var(--white)', visibleTime = 5000) {
    if (createNotificationFunction.current) createNotificationFunction.current(content, bgColor, color, visibleTime);
  }

  return (
    <CookiesProvider>
      <AppContext.Provider
        value={{
          setModalContent,
          setModalVisible,
          setCanHideOnClickOutside,
          createNotification,
          setAppLoaderVisible,
          toggleBodyScroll,
          getUserId,
          getUserData,
          setUserId,
          setIsPdfNotificationLoading,
          isPdfNotificationLoading,
        }}>
        <Modal
          visible={modalVisible}
          setVisible={setModalVisible}
          title={modalContent.title}
          canHideOnClickOutside={canHideOnClickOutside}>
          {modalContent.content}
          {modalContent.buttons && <div className='modal-buttons'>{modalContent.buttons}</div>}
        </Modal>
        <Notification createNotificationFunction={createNotificationFunction} />
        <NotificationDownloadPdf isLoading={isPdfNotificationLoading} />
        <Loader visible={appLoaderVisible || mustAppLoaderShow} className='app-loader' />
        <HelmetProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}>
            <Layout>
              <Routes>
                <Route path='/access' element={<Access />} />
                <Route
                  path='/*'
                  element={
                    <RequireCode>
                      <Routes>
                        {/*Autres */}
                        <Route path='/liste-activites' element={<ActivityList />} />
                        <Route path='/pdf/:estimationIdParam' element={<Pdf />} />

                        {/*Liens légaux */}
                        <Route path='/politique-confidentialite' element={<PolitiqueConfidentialite />} />
                        <Route path='/mentions-legales' element={<MentionsLegales />} />
                        <Route path='/parametrage-cookies' element={<ParametrageCookies />} />
                        <Route path='/cgu' element={<Cgu />} />
                        <Route path='/cgv' element={<Cgv />} />

                        {/*Estimations */}
                        <Route path='/synthese/:estimationIdParam' element={<Overview />} />
                        <Route
                          path='/detail-parcours/:estimationTypeParam/:estimationIdParam'
                          element={<DetailParcours />}
                        />
                        <Route path='/detail-parcours/:estimationTypeParam' element={<DetailParcours />} />

                        {/*Compte */}
                        <Route path='/' element={<Login />} />
                        <Route path='/mon-compte/:tabParam' element={<Account />} />
                        <Route path='/connexion' element={<Login />} />

                        {/*Errors */}
                        <Route path='/301' element={<ErrorPage code={301} />} />
                        <Route path='/302' element={<ErrorPage code={302} />} />
                        <Route path='/401' element={<ErrorPage code={401} />} />
                        <Route path='/402' element={<ErrorPage code={402} />} />
                        <Route path='/403' element={<ErrorPage code={403} />} />
                        <Route path='/404' element={<ErrorPage code={404} />} />
                        <Route path='/500' element={<ErrorPage code={500} />} />
                        <Route path='/503' element={<ErrorPage code={503} />} />
                        <Route path='/504' element={<ErrorPage code={504} />} />

                        <Route path='*' element={<Navigate to='/404' replace />} />
                      </Routes>
                    </RequireCode>
                  }
                />
              </Routes>
            </Layout>
          </Router>
        </HelmetProvider>
      </AppContext.Provider>
    </CookiesProvider>
  );
}

export default App;
